import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createTenantUser,
  deleteTenantUser,
  getTenantUser,
  getTenantUsers,
  updateTenantUser,
} from '@/api/apiTenantUsers';
import type { TenantUserPayload } from '@/api/apiTypes';

async function invalidateTenantUserQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantId?: number,
) {
  await queryClient.invalidateQueries({ queryKey: ['tenant-users', 'list'] });

  if (tenantId) {
    await queryClient.invalidateQueries({ queryKey: ['tenant-users', 'list', tenantId] });
  }
}

export function useTenantUsers(tenantId?: number, pageSize?: number) {
  return useQuery({
    queryKey: ['tenant-users', 'list', tenantId ?? 'all', pageSize ?? 'default'],
    queryFn: () => getTenantUsers({ tenantId, pageSize }),
  });
}

export function useTenantUser(id?: number) {
  return useQuery({
    queryKey: ['tenant-users', 'detail', id],
    queryFn: () => getTenantUser(id as number),
    enabled: id !== undefined,
  });
}

export function useCreateTenantUser(tenantId?: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TenantUserPayload) => createTenantUser(payload),
    onSuccess: async () => invalidateTenantUserQueries(queryClient, tenantId),
  });
}

export function useUpdateTenantUser(tenantId?: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: TenantUserPayload }) =>
      updateTenantUser(id, payload),
    onSuccess: async () => invalidateTenantUserQueries(queryClient, tenantId),
  });
}

export function useDeleteTenantUser(tenantId?: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteTenantUser(id),
    onSuccess: async () => invalidateTenantUserQueries(queryClient, tenantId),
  });
}